import { getAttributesString, getAttributesStringExcluding, isSelfClosingTag, wrapWithOuterWrappers, hideMathAnnotations } from "../htmlUtils";
/**
 * 將 SynopsisWindow 的 Simple_108_2 HTML 按行數分頁
 * @param {string} htmlString - 原始 HTML 字串
 * @param {number} lineHeight - 行高（像素）
 * @param {number} pageSize - 每頁最大行數
 * @param {HTMLElement} referenceContainer - 用於獲取樣式的參考容器
 * @returns {{htmlString: string, totalPages: number}} 分頁後的 HTML 字串和總頁數
 */
export const splitSynopsisIntoPages_Simple108_2 = async (htmlString, lineHeight, pageSize, referenceContainer) => {
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  
  // 提取外層包裝元素
  const outerWrappers = extractOuterWrappers(doc);
  
  // 找到 .simple .content 元素
  const contentElement = doc.body.querySelector('.simple .content');
  if (!contentElement) {
    // 如果找不到 .simple .content，直接返回原始 HTML
    return {
      htmlString: htmlString,
      totalPages: 1,
    };
  }
  
  // 創建測量容器
  const measureContainer = createMeasureContainer(referenceContainer, outerWrappers);
  
  // 直接找到 ExtendedContentContainer 元素（id="exContainer"）
  const extendedContentContainer = document.getElementById('exContainer');
  if (extendedContentContainer) {
    extendedContentContainer.appendChild(measureContainer);
  } else {
    // 如果找不到 ExtendedContentContainer，則 fallback 到 document.body
    document.body.appendChild(measureContainer);
  }

  try {
    // 找到 .simple 元素
    const simpleElement = doc.body.querySelector('.simple');
    if (!simpleElement) {
      return {
        htmlString: htmlString,
        totalPages: 1,
      };
    }

    // 找到 .content 元素
    const contentElement = simpleElement.querySelector('.content');
    if (!contentElement) {
      return {
        htmlString: htmlString,
        totalPages: 1,
      };
    }
    
    // 解析內容區塊（包括 .imgSize 和 .content）
    const blocks = parseContentBlocks(simpleElement, contentElement);
    
    // 將內容切分成多頁
    const pages = await splitBlocksIntoPages(
      blocks,
      pageSize,
      measureContainer,
      lineHeight,
      doc
    );
    
    // 創建頁面元素
    const pageDivs = [];
    pages.forEach((page) => {
      const pageDiv = doc.createElement('div');
      pageDiv.className = 'synopsis-page';
      pageDiv.innerHTML = page.html;
      pageDivs.push(pageDiv);
    });
    
    // 創建一個臨時容器來收集所有頁面元素
    const container = doc.createElement('div');
    pageDivs.forEach(page => {
      container.appendChild(page);
    });
    
    // 用外層包裝元素包裹所有頁面
    const finalHTML = wrapWithOuterWrappers(container.innerHTML, outerWrappers, doc);
    
    return {
      htmlString: finalHTML,
      totalPages: pages.length || 1,
    };
  } finally {
    // 從父容器中移除 measureContainer
    if (measureContainer.parentNode) {
      measureContainer.parentNode.removeChild(measureContainer);
    }
  }
};

/**
 * 提取外層包裝元素
 * 包括 .simple 和 .content 本身，以及它們的所有父元素
 */
const extractOuterWrappers = (doc) => {
  const wrappers = [];
  const body = doc.body;
  
  // 找到 .simple 元素
  const simpleEl = body.querySelector('.simple');
  const contentEl = body.querySelector('.simple .content');
  
  if (simpleEl) {
    // 先添加 .content 元素（最內層）
    if (contentEl) {
      const contentInfo = {
        tagName: contentEl.tagName.toLowerCase(),
        className: contentEl.className || '',
        id: contentEl.id || '',
        attributes: getAttributesStringExcluding(contentEl, ['class', 'id'])
      };
      wrappers.push(contentInfo);
    }
    
    // 添加 .simple 元素
    const simpleInfo = {
      tagName: simpleEl.tagName.toLowerCase(),
      className: simpleEl.className || '',
      id: simpleEl.id || '',
      attributes: getAttributesStringExcluding(simpleEl, ['class', 'id'])
    };
    wrappers.push(simpleInfo);
    
    // 向上查找所有父元素，直到 body
    let current = simpleEl.parentElement;
    while (current && current !== body && current !== doc.documentElement) {
      const wrapperInfo = {
        tagName: current.tagName.toLowerCase(),
        className: current.className || '',
        id: current.id || '',
        attributes: getAttributesStringExcluding(current, ['class', 'id'])
      };
      
      if (wrapperInfo.className || wrapperInfo.id || 
          ['p', 'div', 'section', 'article'].includes(wrapperInfo.tagName)) {
        wrappers.push(wrapperInfo); // 從內層到外層的順序
      }
      
      current = current.parentElement;
    }
  }
  
  return wrappers;
};


/**
 * 創建用於測量的臨時容器
 */
const createMeasureContainer = (referenceContainer, outerWrappers) => {
  const measureContainer = document.createElement('div');
  const containerStyle = window.getComputedStyle(referenceContainer);
  
  measureContainer.style.position = 'absolute';
  measureContainer.style.visibility = 'hidden';
  measureContainer.style.width = referenceContainer.offsetWidth + 'px';
  measureContainer.style.fontSize = containerStyle.fontSize;
  measureContainer.style.lineHeight = containerStyle.lineHeight;
  measureContainer.style.fontFamily = containerStyle.fontFamily;

  // 強制重排，確保樣式應用
  void measureContainer.offsetHeight;
  
  // 創建外層包裝元素的 DOM 結構
  // outerWrappers 的順序是從內層到外層：[.content, .simple, ...父元素]
  if (outerWrappers && outerWrappers.length > 0) {
    let currentContainer = measureContainer;
    
    // 從內層到外層依次創建包裝元素
    outerWrappers.forEach((wrapper) => {
      const wrapperEl = document.createElement(wrapper.tagName);
      if (wrapper.className) {
        wrapperEl.className = wrapper.className;
      }
      if (wrapper.id) {
        wrapperEl.id = wrapper.id;
      }
      
      if (wrapper.attributes) {
        const parser = new DOMParser();
        const tempDoc = parser.parseFromString(`<div ${wrapper.attributes}></div>`, 'text/html');
        const tempEl = tempDoc.body.firstElementChild;
        if (tempEl) {
          Array.from(tempEl.attributes).forEach(attr => {
            if (attr.name !== 'class' && attr.name !== 'id') {
              wrapperEl.setAttribute(attr.name, attr.value);
            }
          });
        }
      }
      
      currentContainer.appendChild(wrapperEl);
      currentContainer = wrapperEl;
    });
    
    // 最內層的容器（.content）用於放置測量內容
    // 因為 outerWrappers 的第一個元素是 .content，所以 currentContainer 就是 .content
    measureContainer._innerContainer = currentContainer;
  } else {
    // 如果沒有外層包裝，創建 .simple .content 結構
    const simpleEl = document.createElement('div');
    simpleEl.className = 'simple';
    measureContainer.appendChild(simpleEl);

    const contentEl = document.createElement('div');
    contentEl.className = 'content';
    simpleEl.appendChild(contentEl);

    measureContainer._innerContainer = contentEl;
  }

  // 攔截 _innerContainer 的 innerHTML setter，每次寫入後自動移除 MathML annotation
  const originalDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
  Object.defineProperty(measureContainer._innerContainer, 'innerHTML', {
    get() { return originalDescriptor.get.call(this); },
    set(html) {
      originalDescriptor.set.call(this, html);
      hideMathAnnotations(this);
    },
    configurable: true,
  });

  // 在返回前再次強制重排，確保結構完整
  if (measureContainer._innerContainer) {
    void measureContainer._innerContainer.offsetHeight;
  }
  void measureContainer.offsetHeight;

  return measureContainer;
};

/**
 * 解析內容區塊，識別 .imgSize、.subject、.ans_box 等結構
 * @param {HTMLElement} simpleElement - .simple 元素
 * @param {HTMLElement} contentElement - .content 元素
 */
const parseContentBlocks = (simpleElement, contentElement) => {
  const blocks = [];
  
  // 先處理 .imgSize（標題圖片），它與 .content 同層（必須是 .simple 的直接子元素）
  const imgSizeElement = simpleElement.querySelector(':scope > .imgSize');
  if (imgSizeElement) {
    // 檢查後面是否有 <br>
    let imgHTML = imgSizeElement.outerHTML;
    const nextSibling = imgSizeElement.nextSibling;
    if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.tagName === 'BR') {
      imgHTML += nextSibling.outerHTML;
    }
    
    // 創建 .title 區塊，包含 .imgSize
    const titleDiv = document.createElement('div');
    titleDiv.className = 'title';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = imgHTML;
    while (tempDiv.firstChild) {
      titleDiv.appendChild(tempDiv.firstChild);
    }
    
    blocks.push({
      type: 'title',
      element: titleDiv,
      className: 'title'
    });
  }

  const childNodes = Array.from(contentElement.childNodes);
  
  let currentBlock = null;
  
  for (let i = 0; i < childNodes.length; i++) {
    const node = childNodes[i];
    
    // 跳過文字節點（空白）
    if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) {
      continue;
    }
    
    // 處理元素節點
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node;
      
      // 檢查是否為 .title
      if (hasClass(element, 'title')) {
        // 結束當前區塊
        if (currentBlock) {
          blocks.push(currentBlock);
        }
        
        // 開始新的 .title 區塊
        currentBlock = {
          type: 'title',
          element: element.cloneNode(true),
          className: 'title'
        };
        continue;
      }
      
      // 檢查是否為 .subject
      if (hasClass(element, 'subject')) {
        // 結束當前區塊
        if (currentBlock) {
          blocks.push(currentBlock);
        }
        
        // 開始新的 .subject 區塊
        currentBlock = {
          type: 'subject',
          element: element.cloneNode(true),
          className: 'subject'
        };
        continue;
      }
      
      // 檢查是否為 .ans_box
      if (hasClass(element, 'ans_box')) {
        // 結束當前區塊
        if (currentBlock) {
          blocks.push(currentBlock);
        }
        
        // .ans_box 視為一個整體，包括內部的圖片
        // 立即將 .ans_box 添加到 blocks，不設置為 currentBlock
        // 這樣後面的元素就不會被添加到 .ans_box 內部
        blocks.push({
          type: 'ans_box',
          element: element.cloneNode(true),
          className: 'ans_box'
        });
        
        // 重置 currentBlock，確保後面的元素不會被添加到 .ans_box
        currentBlock = null;
        continue;
      }
      
      // 檢查是否為 .imgBox（圖片，不在 .ans_box 內的情況）
      if (hasClass(element, 'imgBox') || hasClass(element, 'img')) {
        // 結束當前區塊
        if (currentBlock) {
          blocks.push(currentBlock);
        }
        
        // 檢查後面是否有 <br>
        let imgHTML = element.outerHTML;
        if (i + 1 < childNodes.length) {
          const nextNode = childNodes[i + 1];
          if (nextNode.nodeType === Node.ELEMENT_NODE && nextNode.tagName === 'BR') {
            imgHTML += nextNode.outerHTML;
            i++; // 跳過 <br>
          }
        }
        
        // 圖片自己獨立一頁
        blocks.push({
          type: 'img',
          html: imgHTML
        });
        
        currentBlock = null;
        continue;
      }
      
      // 其他元素（如 <br>）加入當前區塊
      if (currentBlock) {
        // 將元素加入當前區塊
        const tempDiv = document.createElement('div');
        tempDiv.appendChild(element.cloneNode(true));
        currentBlock.element.appendChild(element.cloneNode(true));
      }
    } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
      // 文字節點加入當前區塊
      if (currentBlock) {
        currentBlock.element.appendChild(node.cloneNode(true));
      }
    }
  }
  
  // 添加最後一個區塊
  if (currentBlock) {
    blocks.push(currentBlock);
  }
  
  return blocks;
};

/**
 * 收集區塊中的文字和元素節點（逐字符）
 * @param {HTMLElement} blockElement - 區塊元素
 * @param {string} blockType - 區塊類型（'ans_box' 或其他）
 */
const collectTextNodesFromBlock = (blockElement, blockType) => {
  const nodes = [];
  
  // 如果是 .ans_box，只收集文字部分（.eol 內的文字）來測量高度
  const isAnsBox = blockType === 'ans_box';
  
  const collect = (node, skipImgBox = false) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text.trim()) {
        // 將文字按照英文單字和中文/其他字符分組
        // 英文單字：連續的字母、數字、連字符、撇號等
        // 中文和其他字符：逐字符處理
        const wordPattern = /[\w'-]+/g; // 匹配英文單字（包括字母、數字、連字符、撇號）
        let lastIndex = 0;
        let match;
        
        while ((match = wordPattern.exec(text)) !== null) {
          // 添加英文單字前的非英文字符（逐字符）
          if (match.index > lastIndex) {
            const nonWordText = text.substring(lastIndex, match.index);
            for (const char of nonWordText) {
              nodes.push({ 
                type: 'text', 
                content: char
              });
            }
          }
          
          // 添加英文單字作為整體
          nodes.push({ 
            type: 'text', 
            content: match[0]
          });
          
          lastIndex = match.index + match[0].length;
        }
        
        // 添加剩餘的非英文字符（逐字符）
        if (lastIndex < text.length) {
          const remainingText = text.substring(lastIndex);
          for (const char of remainingText) {
            nodes.push({ 
              type: 'text', 
              content: char
            });
          }
        }
      } else if (text) {
        nodes.push({ 
          type: 'text', 
          content: text
        });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.getAttribute && node.getAttribute('aria-hidden') === 'true') {
        return;
      }
      // 如果是 .ans_box，跳過 .imgBox（不收集用於測量）
      if (isAnsBox && hasClass(node, 'imgBox')) {
        // 不收集 .imgBox，但保留其結構標記
        nodes.push({
          type: 'img_placeholder',
          content: '' // 空內容，不參與高度測量
        });
        return;
      }
      
      // 檢查是否為 .imgBox（圖片，不在 .ans_box 內的情況）
      if (!isAnsBox && hasClass(node, 'imgBox')) {
        nodes.push({
          type: 'img',
          content: node.outerHTML
        });
        return;
      }
      
      // 對於自閉合標籤（如 <br>），直接保留
      if (isSelfClosingTag(node.tagName)) {
        nodes.push({ 
          type: 'element', 
          content: node.outerHTML
        });
        return;
      }
      
      // 對於容器元素，保留標籤結構
      const startTag = `<${node.tagName.toLowerCase()}${getAttributesString(node)}>`;
      nodes.push({ 
        type: 'element', 
        content: startTag
      });
      
      // 遞迴處理子節點
      Array.from(node.childNodes).forEach(child => {
        collect(child, skipImgBox);
      });
      
      // 添加結束標籤
      nodes.push({ 
        type: 'element', 
        content: `</${node.tagName.toLowerCase()}>`
      });
    }
  };
  
  // 如果是 .ans_box，只收集 .eol 內的內容
  if (isAnsBox) {
    const eolElement = blockElement.querySelector('.eol');
    if (eolElement) {
      Array.from(eolElement.childNodes).forEach(child => {
        collect(child);
      });
    }
  } else {
    Array.from(blockElement.childNodes).forEach(child => {
      collect(child);
    });
  }
  
  return nodes;
};




/**
 * 將區塊切分成多頁
 */
const splitBlocksIntoPages = async (blocks, pageSize, measureContainer, lineHeight, doc) => {
  const pages = [];
  let currentPageBlocks = []; // 當前頁包含的區塊，每個區塊包含其內容
  let currentPageBlockContents = {}; // 追蹤每個區塊的內容 { blockId: content }
  let ansBoxFirstPage = true; // 標記 .ans_box 是否為第一頁
  
  // 如果測量容器有 _innerContainer，將 HTML 放入內層容器
  const contentContainer = measureContainer._innerContainer || measureContainer;
  
  // 每一頁的最大高度（7 行）
  const maxHeight = pageSize * lineHeight;
  
  // 生成區塊的唯一 ID
  let blockIdCounter = 0;
  
  // 建立 .subject 和 .ans_box 的配對關係
  const subjectAnsBoxPairs = new Map(); // { ansBoxBlockId: subjectBlockId }
  let lastSubjectBlockId = null;
  
  // 先遍歷一次，建立配對關係
  blocks.forEach((block) => {
    const blockId = blockIdCounter++;
    block._id = blockId;
    
    // 如果是 .subject，記錄它
    if (block.type === 'subject') {
      lastSubjectBlockId = blockId;
    }
    
    // 如果是 .ans_box，且前面有 .subject，建立配對關係
    if (block.type === 'ans_box' && lastSubjectBlockId !== null) {
      subjectAnsBoxPairs.set(blockId, lastSubjectBlockId);
      lastSubjectBlockId = null; // 重置，一個 .subject 只能配對一個 .ans_box
    }
  });
  
  // 重置 counter，重新遍歷進行分頁
  blockIdCounter = 0;
  
  for (const block of blocks) {
    // 為區塊分配唯一 ID（已經在第一次遍歷時分配了）
    const blockId = block._id;
    
    // 如果遇到圖片，圖片自己佔一頁
    if (block.type === 'img') {
      // 如果當前頁有內容，先保存當前頁
      if (currentPageBlocks.length > 0) {
        const wrappedCurrentPageHTML = buildFinalHTMLFromBlocks(currentPageBlocks, currentPageBlockContents, true);
        pages.push({
          html: wrappedCurrentPageHTML
        });
        currentPageBlocks = [];
        currentPageBlockContents = {};
        ansBoxFirstPage = true; // 重置為第一頁
      }
      
      // 圖片自己佔一頁
      pages.push({
        html: block.html
      });
      return;
    }
    
    // 如果是新的 .ans_box 區塊，重置標記
    if (block.type === 'ans_box' && !currentPageBlocks.find(b => b._id === blockId)) {
      ansBoxFirstPage = true;
    }
    
    // 檢查當前區塊是否是 .ans_box，且是否有配對的 .subject
    const pairedSubjectId = subjectAnsBoxPairs.get(blockId);
    const hasPairedSubject = pairedSubjectId !== undefined && block.type === 'ans_box';
    
    // 如果 .ans_box 有配對的 .subject，且 .subject 在當前頁，則檢查是否會超過七行
    if (hasPairedSubject) {
      const subjectInCurrentPage = currentPageBlocks.find(b => b._id === pairedSubjectId);
      
      if (subjectInCurrentPage && currentPageBlocks.length > 0) {
        // 先測試將整個 .ans_box 的文字部分加入當前頁的高度
        const ansBoxTextContent = collectTextNodesFromBlock(block.element, 'ans_box');
        let ansBoxTextHTML = '';
        ansBoxTextContent.forEach(node => {
          if (node.type !== 'img_placeholder' && node.content) {
            ansBoxTextHTML += node.content;
          }
        });
        
        // 構建測試 HTML（包含當前頁的所有區塊和新的 .ans_box）
        const testBlocks = [...currentPageBlocks, block];
        const testBlockContents = { ...currentPageBlockContents };
        testBlockContents[blockId] = ansBoxTextHTML;
        const testHTML = buildTestHTMLForMeasurementFromBlocks(testBlocks, testBlockContents, block);
        // 使用統一的測量函數，確保時序一致
        const testHeight = await measureHeightAsync(measureContainer, contentContainer, testHTML);
        
        // 如果會超過七行，則 .subject 和 .ans_box 一起新開一頁
        if (testHeight > maxHeight + 1) {
          // 從當前頁移除配對的 .subject
          const subjectContent = currentPageBlockContents[pairedSubjectId] || '';
          delete currentPageBlockContents[pairedSubjectId];
          currentPageBlocks = currentPageBlocks.filter(b => b._id !== pairedSubjectId);
          
          // 先保存當前頁（不包含 .subject）
          const wrappedCurrentPageHTML = buildFinalHTMLFromBlocks(currentPageBlocks, currentPageBlockContents, true);
          pages.push({
            html: wrappedCurrentPageHTML
          });
          
          // 新頁包含 .subject 和 .ans_box
          currentPageBlocks = [subjectInCurrentPage, block];
          currentPageBlockContents = {
            [pairedSubjectId]: subjectContent,
            [blockId]: ''
          };
          ansBoxFirstPage = true; // 重置為第一頁
        }
      }
    }
    
    // 初始化區塊內容
    if (!currentPageBlockContents[blockId]) {
      currentPageBlockContents[blockId] = '';
    }
    
    // 收集區塊中的文字和元素節點（對於 .ans_box，只收集文字部分）
    const textContent = collectTextNodesFromBlock(block.element, block.type);
    
    // 如果區塊還沒加入，先加入（確保即使沒有內容也會被處理）
    if (!currentPageBlocks.find(b => b._id === blockId)) {
      currentPageBlocks.push(block);
    }
    
    // 如果 textContent 為空，但區塊類型是 .subject 或 .title，至少保留區塊結構
    if (textContent.length === 0 && (block.type === 'subject' || block.type === 'title')) {
      // 對於空的 .subject 或 .title，至少保留其 outerHTML 結構
      const blockHTML = block.element.outerHTML;
      currentPageBlockContents[blockId] = blockHTML;
      // 不需要繼續處理，因為已經有內容了
      continue;
    }

    // 逐字符處理
    for (const node of textContent) {
      const nodeContent = node.content;
      const nodeType = node.type;
      
      // 跳過 .ans_box 中的圖片佔位符（不參與測量）
      if (nodeType === 'img_placeholder') {
        return;
      }
      
      if (!nodeContent && nodeType !== 'img_placeholder') return;
      
      // 如果遇到圖片節點（不在 .ans_box 內），圖片自己佔一頁
      if (nodeType === 'img') {
        // 如果當前頁有內容，先保存當前頁
        if (currentPageBlocks.length > 0) {
          const wrappedCurrentPageHTML = buildFinalHTMLFromBlocks(currentPageBlocks, currentPageBlockContents, ansBoxFirstPage);
          pages.push({
            html: wrappedCurrentPageHTML
          });
          currentPageBlocks = [];
          currentPageBlockContents = {};
        }
        
        // 圖片自己佔一頁
        pages.push({
          html: nodeContent
        });
        return;
      }
      
      // 更新當前區塊的內容
      currentPageBlockContents[blockId] += nodeContent;
      
      // 如果區塊還沒加入，則加入
      if (!currentPageBlocks.find(b => b._id === blockId)) {
        currentPageBlocks.push(block);
      }
      
      // 構建測試 HTML（需要包含當前頁的所有區塊）
      const testHTML = buildTestHTMLForMeasurementFromBlocks(currentPageBlocks, currentPageBlockContents, block);
      
      // 使用統一的測量函數，確保時序一致
      const testHeight = measureHeight(measureContainer, contentContainer, testHTML);

      if (testHeight > maxHeight + 1 && currentPageBlocks.length > 0) {
        // 超過限制，切到新頁
        // 移除當前字符（因為它會在新頁）
        currentPageBlockContents[blockId] = currentPageBlockContents[blockId].slice(0, -nodeContent.length);
        
        // 檢查當前區塊是否是 .ans_box，且是否有配對的 .subject
        const pairedSubjectId = subjectAnsBoxPairs.get(blockId);
        const hasPairedSubject = pairedSubjectId !== undefined && block.type === 'ans_box';
        
        // 準備要移到新頁的區塊和內容
        let newPageBlocks = [block];
        let newPageBlockContents = { [blockId]: nodeContent };
        
        // 如果當前區塊是 .ans_box 且有配對的 .subject，檢查 .subject 是否在當前頁
        if (hasPairedSubject) {
          const subjectInCurrentPage = currentPageBlocks.find(b => b._id === pairedSubjectId);
          
          // 如果 .subject 在當前頁，需要將它一起移到新頁
          if (subjectInCurrentPage) {
            const subjectContent = currentPageBlockContents[pairedSubjectId] || '';
            
            // 從當前頁移除 .subject
            delete currentPageBlockContents[pairedSubjectId];
            currentPageBlocks = currentPageBlocks.filter(b => b._id !== pairedSubjectId);
            
            // 新頁包含 .subject 和 .ans_box
            newPageBlocks = [subjectInCurrentPage, block];
            newPageBlockContents = {
              [pairedSubjectId]: subjectContent,
              [blockId]: nodeContent
            };
          }
        }
        
        // 如果區塊內容為空，從當前頁移除
        if (!currentPageBlockContents[blockId]) {
          delete currentPageBlockContents[blockId];
          currentPageBlocks = currentPageBlocks.filter(b => b._id !== blockId);
        }
        
        // 用原始元素的結構包裹當前頁內容（保持完整結構）
        const wrappedCurrentPageHTML = buildFinalHTMLFromBlocks(currentPageBlocks, currentPageBlockContents, ansBoxFirstPage);
        pages.push({
          html: wrappedCurrentPageHTML
        });
        
        // 開始新頁
        currentPageBlocks = newPageBlocks;
        currentPageBlockContents = newPageBlockContents;
        // 如果是 .ans_box，標記為非第一頁
        if (block.type === 'ans_box') {
          ansBoxFirstPage = false;
        }
      }
    };
  };
  
  // 最後一頁
  if (currentPageBlocks.length > 0) {
    // 檢查最後一頁是否包含 .ans_box
    const lastPageHasAnsBox = currentPageBlocks.some(b => b.type === 'ans_box');
    const isLastPageFirstPage = lastPageHasAnsBox && ansBoxFirstPage;
    const wrappedLastPageHTML = buildFinalHTMLFromBlocks(currentPageBlocks, currentPageBlockContents, isLastPageFirstPage);
    pages.push({
      html: wrappedLastPageHTML
    });
  }
  
  // 如果最後一頁只有一行，合併到上一頁
  if (pages.length > 1) {
    const lastPage = pages[pages.length - 1];
    const lastPageHTML = lastPage.html;
    
    // 直接用 lastPageHTML 測量，它已是 buildFinalHTMLFromBlocks 產出的完整 HTML
    const lastPageHeight = await measureHeightAsync(measureContainer, contentContainer, lastPageHTML);

    
    // 如果最後一頁的高度小於等於一行，合併到上一頁
    if (lastPageHeight <= lineHeight + 1) {
      const previousPage = pages[pages.length - 2];
      previousPage.html += lastPageHTML;
      pages.pop();
    }
  }
  
  return pages.length > 0 ? pages : [{ html: '' }];
};

/**
 * 構建最終輸出 HTML（從區塊和內容構建）
 * 當多個區塊在同一頁時，需要分別包裹每個區塊
 * 用於最終輸出，保持完整結構
 * @param {Array} blocks - 區塊陣列
 * @param {Object} blockContents - 區塊內容 { blockId: content }
 * @param {boolean} includeImgBox - 是否包含 .imgBox（用於 .ans_box 的第一頁）
 */
const buildFinalHTMLFromBlocks = (blocks, blockContents, includeImgBox = true) => {
  if (!blocks || blocks.length === 0) {
    return '';
  }
  
  let html = '';
  
  // 按順序構建每個區塊
  blocks.forEach(block => {
    const blockContent = blockContents[block._id] || '';
    
    if (block.type === 'title') {
      html += wrapBlockContent(blockContent, 'title');
    } else if (block.type === 'subject') {
      html += wrapBlockContent(blockContent, 'subject');
    } else if (block.type === 'ans_box') {
      html += buildAnsBoxHTML(blockContent, block.element, includeImgBox);
    }
  });
  
  return html;
};

/**
 * 構建測試 HTML（用於測量高度）
 * 從區塊和內容構建，對於 .ans_box，只測量文字部分，但保持 flex 結構
 */
const buildTestHTMLForMeasurementFromBlocks = (blocks, blockContents, currentBlock) => {
  let testHTML = '';
  
  // 按順序構建每個區塊
  blocks.forEach(block => {
    const blockContent = blockContents[block._id] || '';
    
    if (block.type === 'title') {
      testHTML += wrapBlockContent(blockContent, 'title');
    } else if (block.type === 'subject') {
      testHTML += wrapBlockContent(blockContent, 'subject');
    } else if (block.type === 'ans_box') {
      // 構建完整的 .ans_box 結構（包括 .imgBox），但只測量 .eol 內文字的高度
      const ansBoxClone = block.element.cloneNode(true);
      const imgBox = ansBoxClone.querySelector('.imgBox');
      const eolElement = ansBoxClone.querySelector('.eol.ANSWERS');
      
      if (eolElement) {
        // 保持 .ans_box 的 flex 結構
        testHTML += '<div class="ans_box" style="display: flex; align-items: flex-start;">';
        
        // 如果有 .imgBox，保留它（但不測量其高度）
        if (imgBox) {
          testHTML += imgBox.outerHTML;
        }
        
        // 添加 .eol 和文字內容（只測量這部分的高度）
        const eolAttrs = getAttributesString(eolElement);
        testHTML += `<div class="eol ANSWERS"${eolAttrs}>${blockContent}</div>`;
        testHTML += '</div>';
      }
    }
  });
  
  // 如果當前區塊還沒加入，添加它
  if (currentBlock && !blocks.find(b => b._id === currentBlock._id)) {
    const blockContent = blockContents[currentBlock._id] || '';
    if (currentBlock.type === 'ans_box') {
      const ansBoxClone = currentBlock.element.cloneNode(true);
      const imgBox = ansBoxClone.querySelector('.imgBox');
      const eolElement = ansBoxClone.querySelector('.eol.ANSWERS');
      
      if (eolElement) {
        testHTML += '<div class="ans_box" style="display: flex; align-items: flex-start;">';
        if (imgBox) {
          testHTML += imgBox.outerHTML;
        }
        const eolAttrs = getAttributesString(eolElement);
        testHTML += `<div class="eol ANSWERS"${eolAttrs}>${blockContent}</div>`;
        testHTML += '</div>';
      }
    } else {
      testHTML += wrapBlockContent(blockContent, currentBlock.className);
    }
  }
  
  return testHTML;
};

/**
 * 構建 .ans_box 的完整 HTML 結構（保持 flex 結構）
 * @param {string} eolContent - .eol 內的內容
 * @param {HTMLElement} originalAnsBoxElement - 原始的 .ans_box 元素
 * @param {boolean} includeImgBox - 是否包含 .imgBox（第一頁包含，後續頁不包含）
 */
const buildAnsBoxHTML = (eolContent, originalAnsBoxElement, includeImgBox = true) => {
  const ansBoxClone = originalAnsBoxElement.cloneNode(true);
  const imgBox = ansBoxClone.querySelector('.imgBox');
  const eolElement = ansBoxClone.querySelector('.eol.ANSWERS');
  
  // 保持 .ans_box 的 flex 結構
  let html = '<div class="ans_box" style="display: flex; align-items: flex-start;">';
  
  // 如果有 .imgBox 且是第一頁，保留它
  if (imgBox && includeImgBox) {
    html += imgBox.outerHTML;
  }
  
  // 添加 .eol 和內容
  if (eolElement) {
    // 保留 .eol 的原始屬性
    const eolAttrs = getAttributesString(eolElement);
    // 確保 eolContent 不包含重複的 .eol 標籤
    // 如果 eolContent 已經包含了 .eol 標籤，則提取其內容
    let cleanContent = eolContent;
    if (eolContent.includes('<div class="eol')) {
      // 如果內容已經包含了 .eol div，提取其內部內容
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = eolContent;
      const existingEol = tempDiv.querySelector('.eol');
      if (existingEol) {
        cleanContent = existingEol.innerHTML;
      }
    }
    html += `<div class="eol ANSWERS"${eolAttrs}>${cleanContent}</div>`;
  } else {
    // 確保 eolContent 不包含重複的 .eol 標籤
    let cleanContent = eolContent;
    if (eolContent.includes('<div class="eol')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = eolContent;
      const existingEol = tempDiv.querySelector('.eol');
      if (existingEol) {
        cleanContent = existingEol.innerHTML;
      }
    }
    html += `<div class="eol ANSWERS">${cleanContent}</div>`;
  }
  
  html += '</div>';
  
  return html;
};

/**
 * 用原始元素的結構包裹內容
 */
const wrapBlockContent = (content, className) => {
  if (!className) {
    return content;
  }
  
  return `<div class="${className}">${content}</div>`;
};

/**
 * 檢查元素是否包含指定的 class
 * 可以處理標準寫法 class="subject" 和非標準寫法 class subject
 * @param {HTMLElement} element - 要檢查的元素
 * @param {string} className - 要檢查的 class 名稱
 * @returns {boolean} 是否包含該 class
 */
const hasClass = (element, className) => {
  if (!element || !className) return false;
  
  // 方法 1: 使用 classList（標準寫法 class="subject"）
  if (element.classList && element.classList.contains(className)) {
    return true;
  }
  
  // 方法 2: 檢查 className 屬性（處理 class="subject" 和 class subject）
  if (element.className) {
    const classAttr = element.className;
    // 使用正則表達式匹配完整的 class 名稱（避免部分匹配）
    const classRegex = new RegExp(`(^|\\s)${className}(\\s|$)`);
    if (classRegex.test(classAttr)) {
      return true;
    }
  }
  
  // 方法 3: 檢查 getAttribute('class')（處理非標準寫法）
  const classAttr = element.getAttribute('class');
  if (classAttr) {
    const classRegex = new RegExp(`(^|\\s)${className}(\\s|$)`);
    if (classRegex.test(classAttr)) {
      return true;
    }
  }
  
  // 方法 4: 直接檢查 attributes 集合（處理非標準寫法 class subject）
  if (element.attributes) {
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      // 檢查屬性名稱是否為 'class'，且值包含目標 class
      if (attr.name === 'class' && attr.value) {
        const classRegex = new RegExp(`(^|\\s)${className}(\\s|$)`);
        if (classRegex.test(attr.value)) {
          return true;
        }
      }
      // 對於非標準寫法 class subject，屬性名稱可能就是 class 名稱
      // 例如：<div class subject> 可能被解析為 attr.name = 'class', attr.value = 'subject'
      // 或者 attr.name = 'subject', attr.value = ''
      if (attr.name === className && (attr.value === '' || attr.value === className)) {
        return true;
      }
    }
  }
  
  // 方法 5: 檢查 outerHTML（最後的手段，處理極端情況）
  const outerHTML = element.outerHTML || '';
  // 匹配 <div class="subject"> 或 <div class subject> 或 <div class="... subject ...">
  const htmlRegex = new RegExp(`class\\s*=\\s*["']?[^"'>]*\\b${className}\\b[^"'>]*["']?|class\\s+${className}(?:\\s|>)`, 'i');
  if (htmlRegex.test(outerHTML)) {
    return true;
  }
  
  return false;
};

/**
 * 強制瀏覽器重排，確保 DOM 更新完成後再測量
 * @param {HTMLElement} element - 要強制重排的元素
 */
const forceReflow = (element) => {
  // 方法 1: 讀取 offsetHeight 強制重排
  void element.offsetHeight;
  
  // 方法 2: 讀取 getComputedStyle 強制重排
  void window.getComputedStyle(element).height;
  
  // 方法 3: 讀取 scrollHeight 強制重排
  void element.scrollHeight;
};

/**
 * 測量容器高度（確保 DOM 更新完成）
 * @param {HTMLElement} measureContainer - 測量容器
 * @param {HTMLElement} contentContainer - 內容容器
 * @param {string} testHTML - 測試 HTML
 * @returns {number} 測量的高度
 */
const measureHeight = (measureContainer, contentContainer, testHTML) => {
  // 設置 HTML
  contentContainer.innerHTML = testHTML;
  
  // 強制重排，確保 DOM 更新完成
  forceReflow(measureContainer);
  forceReflow(contentContainer);
  
  // 等待一幀，確保樣式應用完成
  // 注意：這裡不能使用 async/await，因為會改變函數簽名
  // 所以使用同步方式強制重排
  
  // 再次強制重排，確保測量準確
  void measureContainer.offsetHeight;
  void contentContainer.offsetHeight;
  
  // 測量高度
  const height = measureContainer.offsetHeight;
  
  return height;
};

/**
 * 等待 DOM 更新完成後測量高度
 * @param {HTMLElement} measureContainer - 測量容器
 * @param {HTMLElement} contentContainer - 內容容器
 * @param {string} testHTML - 測試 HTML
 * @returns {Promise<number>} 測量的高度
 */
const measureHeightAsync = (measureContainer, contentContainer, testHTML) => {
  return new Promise((resolve) => {
    // 設置 HTML
    contentContainer.innerHTML = testHTML;
    
    // 使用 requestAnimationFrame 確保 DOM 更新完成
    requestAnimationFrame(() => {
      // 再次使用 requestAnimationFrame 確保樣式應用完成
      requestAnimationFrame(() => {
        // 強制重排
        void measureContainer.offsetHeight;
        void contentContainer.offsetHeight;
        
        // 測量高度
        const height = measureContainer.offsetHeight;
        resolve(height);
      });
    });
  });
};

